import React, { useState, useEffect } from 'react';
import type { FieldProps } from '../types';

/**
 * NumberField Component
 *
 * Renders number input with min/max/decimal validation.
 */
export const NumberField: React.FC<FieldProps> = ({
  element,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  theme,
}) => {
  const properties = element.properties;
  const [localValue, setLocalValue] = useState(value?.toString() || '');

  useEffect(() => {
    setLocalValue(value?.toString() || '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Convert to number if valid
    if (newValue === '') {
      onChange(undefined);
    } else {
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue)) {
        onChange(numValue);
      }
    }
  };

  const handleBlur = () => {
    // Format decimal places on blur if specified
    if (localValue && properties?.decimalPlaces !== undefined) {
      const numValue = parseFloat(localValue);
      if (!isNaN(numValue)) {
        const formatted = numValue.toFixed(properties.decimalPlaces);
        setLocalValue(formatted);
        onChange(parseFloat(formatted));
      }
    }
    onBlur?.();
  };

  // Calculate step based on decimal places
  const step = properties?.decimalPlaces !== undefined
    ? Math.pow(10, -properties.decimalPlaces)
    : 'any';

  return (
    <div className="mediform-field mediform-number-field">
      {properties?.label && (
        <label htmlFor={element.id} className="mediform-field-label">
          {properties.label}
          {properties.required && <span className="mediform-required"> *</span>}
        </label>
      )}

      <div className="mediform-field-input">
        <input
          id={element.id}
          type="number"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          required={properties?.required}
          min={properties?.min}
          max={properties?.max}
          step={step}
          placeholder={properties?.label}
          className={`mediform-input ${error ? 'mediform-input-error' : ''}`}
          style={{
            borderColor: error ? theme?.colors?.error : theme?.colors?.border,
          }}
        />
      </div>

      {error && (
        <div className="mediform-field-error" style={{ color: theme?.colors?.error }}>
          {error}
        </div>
      )}

      {properties?.min !== undefined && properties?.max !== undefined && (
        <div className="mediform-field-hint">
          Range: {properties.min} - {properties.max}
        </div>
      )}
    </div>
  );
};

export default NumberField;
